/**
 * @see https://github.com/akserg/ng2-slim-loading-bar/tree/master
 * @description This service was simplified and updated to Angular 17 based on the component linked above.
 */

import {Injectable} from '@angular/core';
import {Observable, Subject} from 'rxjs';
import {LoadingIndicatorEventTypeEnum} from '../enum/loading-indicator-event-type.enum';
import {LoadingIndicatorEvent} from '../event/loading-indicator.event';

/**
 * @class
 * @description This class provides methods to interact with the loading-indicator service.
 * @export
 * @name LoadingIndicatorService
 * @service
 * @singleton
 */
@Injectable({
  providedIn: 'root'
})
export class LoadingIndicatorService {

  /**
   * @description This property contains the event source of the loading-indicator service.
   * @memberof LoadingIndicatorService
   * @name LoadingIndicatorService#eventSource
   * @private
   * @property
   * @type {Subject<LoadingIndicatorEvent>}
   */
  private eventSource: Subject<LoadingIndicatorEvent> = new Subject<LoadingIndicatorEvent>();

  /**
   * @description This property contains the interval [in milliseconds] for the loading-indicator service to wait until progress is automatically incremented.
   * @memberof LoadingIndicatorService
   * @name LoadingIndicatorService#incrementInterval
   * @private
   * @property
   * @type {number}
   */
  private incrementInterval: number = 500;

  /**
   * @description This property contains the interval for the loading-indicator service.
   * @memberof LoadingIndicatorService
   * @name LoadingIndicatorService#intervalCounter
   * @private
   * @property
   * @type {NodeJS.Timeout}
   */
  private intervalCounter: any = null;

  /**
   * @description This property contains the progress of the loading-indicator service.
   * @memberof LoadingIndicatorService
   * @name LoadingIndicatorService#progressValue
   * @private
   * @property
   * @type {number}
   */
  private progressValue: number = 0;

  /**
   * @description This property contains the observable of the loading-indicator service.
   * @memberof LoadingIndicatorService
   * @name LoadingIndicatorService#events
   * @property
   * @public
   * @type {Observable<LoadingIndicatorEvent>}
   */
  public events: Observable<LoadingIndicatorEvent> = this.eventSource.asObservable();

  /**
   * @constructor
   * @description This method instantiates a new instance of our loading-indicator service provider.
   * @memberof LoadingIndicatorService
   * @name LoadingIndicatorService#constructor
   * @public
   */
  public constructor() {
  }

  /**
   * @description This method emits an event related to the loading-indicator indicator.
   * @memberof LoadingIndicatorService
   * @method
   * @name LoadingIndicatorService#emitEvent()
   * @param {LoadingIndicatorEvent} event The event to emit.
   * @private
   * @returns {void}
   */
  private emitEvent(event: LoadingIndicatorEvent): void {

    // Check for an event source then emit the event.
    if (this.eventSource) this.eventSource.next(event);
  }

  /**
   * @description This method completes the loading-indicator indicator by setting the progress to 100% and stopping the loading-indicator indicator.
   * @memberof LoadingIndicatorService
   * @method
   * @name LoadingIndicatorService#complete()
   * @param {boolean} skipEvent Whether or not to skip the event.
   * @public
   * @returns {void}
   * @see LoadingIndicatorService#progress()
   * @see LoadingIndicatorService#reset()
   * @see LoadingIndicatorService#stop()
   */
  public complete(skipEvent: boolean = false): void {

    // Set the progress to 100% to complete the animation.
    this.progress(100);

    // Stop the loading-indicator indicator.
    this.stop(false);

    // Set our timeout to reset the loading-indicator indicator.
    setTimeout((): void => this.reset(false), 250);

    // Emit the complete event if we're not skipping the event.
    if (!skipEvent) this.emitEvent(new LoadingIndicatorEvent(LoadingIndicatorEventTypeEnum.Complete, this.progressValue));
  }

  /**
   * @description This method triggers an error state for the loading-indicator indicator.
   * @memberof LoadingIndicatorService
   * @method
   * @param {Error} error The error to pass along with the event that gets emitted.
   * @param {boolean} skipEvent Whether or not to skip the event.
   * @public
   * @returns {void}
   */
  public error(error: Error, skipEvent: boolean = false): void {

    // Stop the loading-indicator indicator.
    this.stop(false);

    // Emit the error event if we're not skipping the event.
    if (!skipEvent) this.emitEvent(new LoadingIndicatorEvent(LoadingIndicatorEventTypeEnum.Error, error));
  }

  /**
   * @description This method shows the loading-indicator indicator.
   * @memberof LoadingIndicatorService
   * @method
   * @name LoadingIndicatorService#show()
   * @param {boolean} skipEvent Whether or not to skip the event.
   * @public
   * @returns {void}
   */
  public hide(skipEvent: boolean = false): void {

    // Emit the hidden event if we're not skipping the event.
    if (!skipEvent) this.emitEvent(new LoadingIndicatorEvent(LoadingIndicatorEventTypeEnum.Hidden, this.progressValue));
  }

  /**
   * @description This method returns the progress of the loading-indicator indicator and optionally sets the current progress of the loading-indicator indicator service.
   * @memberof LoadingIndicatorService
   * @method
   * @name LoadingIndicatorService#progress()
   * @param {number?} value The optional value to set the progress to.
   * @param {boolean} skipEvent Whether or not to skip the event.
   * @public
   * @returns {number} The current progress of the loading-indicator indicator.
   */
  public progress(value: number | undefined = undefined, skipEvent: boolean = false): number {

    // Check for a provided value then reset the progress value into the instance.
    if (value !== undefined && value > -1) {

      // Reset the progress value into the instance.
      this.progressValue = value;

      // Emit the progress event if we're not skipping the event.
      if (!skipEvent) this.emitEvent(new LoadingIndicatorEvent(LoadingIndicatorEventTypeEnum.Progress, this.progressValue));
    }

    // We're done, return the progress.
    return this.progressValue;
  }

  /**
   * @description This method resets the loading-indicator service by stopping any running loading-indicator indicators and resetting the progress to 0.
   * @memberof LoadingIndicatorService
   * @method
   * @name LoadingIndicatorService#reset()
   * @public
   * @returns {void}
   * @see LoadingIndicatorService#progress()
   * @see LoadingIndicatorService#stop()
   */
  public reset(skipEvent: boolean = false): void {

    // Stop any running loading-indicator indicators.
    this.stop(true, false);

    // Reset the progress to 0.
    this.progress(0, true);

    // Emit the reset event if we're not skipping the event.
    if (!skipEvent) this.emitEvent(new LoadingIndicatorEvent(LoadingIndicatorEventTypeEnum.Reset, this.progressValue));
  }

  /**
   * @description This method shows the loading-indicator indicator.
   * @memberof LoadingIndicatorService
   * @method
   * @name LoadingIndicatorService#show()
   * @param {boolean} skipEvent Whether or not to skip the event.
   * @public
   * @returns {void}
   */
  public show(skipEvent: boolean = false): void {

    // Emit the visible event if we're not skipping the event.
    if (!skipEvent) this.emitEvent(new LoadingIndicatorEvent(LoadingIndicatorEventTypeEnum.Visible, this.progressValue));
  }

  /**
   * @description This method turns the loading-indicator service on by emitting the next value of the subject.
   * @memberof LoadingIndicatorService
   * @method
   * @name LoadingIndicatorService#start()
   * @param {boolean} skipEvent Whether or not to skip the event.
   * @public
   * @returns {void}
   * @see LoadingIndicatorService#progress()
   * @see LoadingIndicatorService#stop()
   */
  public start(skipEvent: boolean = false): void {

    // Turn off any previous loading-indicator indicators.
    this.stop(true, true);

    // Show the loading-indicator indicator.
    this.show(false);

    // Set up our interval to automatically increment the progress.
    this.intervalCounter = setInterval((): void => {

      // Increment the progress.
      this.progress(this.progress() + 1, false);

      // Check for completion then complete the loading-indicator indicator.
      if (this.progress() >= 100) this.complete(false);

    }, this.incrementInterval);

    // Emit the start event if we're not skipping the event.
    if (!skipEvent) this.emitEvent(new LoadingIndicatorEvent(LoadingIndicatorEventTypeEnum.Start, this.progressValue));
  }

  /**
   * @description This method turns the loading-indicator service off by emitting the next value of the subject.
   * @memberof LoadingIndicatorService
   * @method
   * @name LoadingIndicatorService#stop()
   * @param {boolean} skipEvent Whether or not to skip the event.
   * @param {boolean} skipHideEvent Whether or not to skip the hide event.
   * @public
   * @returns {void}
   */
  public stop(skipEvent: boolean = false, skipHideEvent: boolean = false): void {

    // Check for an existing interval counter then clear the interval.
    if (this.intervalCounter) {

      // Clear the interval.
      clearInterval(this.intervalCounter);

      // Reset the interval counter.
      this.intervalCounter = null;
    }

    // Hide the loading-indicator indicator.
    this.hide(skipHideEvent);

    // Emit the stop event if we're not skipping the event.
    if (!skipEvent) this.emitEvent(new LoadingIndicatorEvent(LoadingIndicatorEventTypeEnum.Stop, this.progressValue));
  }
}
